import { PackagePlus, PlusCircle, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useHasPermission } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  useDeleteOpeningStock,
  useOpeningStocks,
} from "@/hooks/use-opening-stock";
import { formatCurrency } from "@/lib/utils";

import { formatDate } from "./opening-stock-utils";

import type { OpeningStockStatus } from "@artisancode/api-types";

export function OpeningStockListPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const canCreate = useHasPermission("opening_stocks.create");
  const canDelete = useHasPermission("opening_stocks.delete");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: response, isLoading } = useOpeningStocks({
    search: search || undefined,
    status:
      statusFilter !== "all" ? (statusFilter as OpeningStockStatus) : undefined,
    perPage: 50,
  });

  const deleteMutation = useDeleteOpeningStock();

  const items = useMemo(() => response?.items ?? [], [response?.items]);

  const metrics = useMemo(() => {
    let completedCount = 0;
    let draftCount = 0;
    let totalValue = 0;

    for (const item of items) {
      if (item.status === "completed") {
        completedCount++;
        totalValue += item.totalCost;
      } else {
        draftCount++;
      }
    }

    return {
      totalDocs: items.length,
      completedCount,
      draftCount,
      totalValue,
    };
  }, [items]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteMutation.mutateAsync(deleteId);
      toast.success("Draft Stok Awal berhasil dihapus");
      setDeleteId(null);
    } catch {
      /* handled globally */
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Stok Awal"
        description="Pencatatan saldo stok awal toko dan penetapan HPP awal barang sebelum operasional."
        action={
          canCreate ? (
            <Button
              size="sm"
              onClick={() => navigate("/opening-stocks/new")}
              className="gap-2"
            >
              <PlusCircle className="size-4" />
              <span>Stok Awal Baru</span>
            </Button>
          ) : undefined
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card className="shadow-xs">
          <CardHeader className="p-4 pb-2">
            <CardDescription className="text-xs">Total Dokumen</CardDescription>
            <CardTitle className="text-xl font-bold">
              {metrics.totalDocs}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="shadow-xs">
          <CardHeader className="p-4 pb-2">
            <CardDescription className="text-xs">
              Dokumen Selesai
            </CardDescription>
            <CardTitle className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
              {metrics.completedCount}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="shadow-xs">
          <CardHeader className="p-4 pb-2">
            <CardDescription className="text-xs">Dokumen Draft</CardDescription>
            <CardTitle className="text-xl font-bold text-amber-600 dark:text-amber-400">
              {metrics.draftCount}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="shadow-xs">
          <CardHeader className="p-4 pb-2">
            <CardDescription className="text-xs">
              Total Nilai Stok Awal
            </CardDescription>
            <CardTitle className="text-xl font-bold text-primary">
              {formatCurrency(metrics.totalValue)}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Main Table Card */}
      <Card className="shadow-xs">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base font-semibold">
                Riwayat Dokumen Stok Awal
              </CardTitle>
              <CardDescription className="text-xs">
                Daftar semua pencatatan saldo awal barang dan penetapan HPP.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative w-full sm:w-60">
                <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
                <Input
                  placeholder="Cari nomor/catatan..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-9 text-xs"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[120px] h-9 text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="completed">Selesai</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 sm:p-6 sm:pt-0">
          {isLoading ? (
            <div className="py-12 text-center text-xs text-muted-foreground">
              Memuat data stok awal...
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-xs text-muted-foreground space-y-2">
              <PackagePlus className="mx-auto size-8 text-muted-foreground/50" />
              <p>Belum ada dokumen Stok Awal.</p>
              {canCreate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/opening-stocks/new")}
                >
                  Buat Dokumen Sekarang
                </Button>
              )}
            </div>
          ) : isMobile ? (
            <div className="divide-y divide-border">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="p-4 space-y-2 cursor-pointer hover:bg-muted/30"
                  onClick={() => navigate(`/opening-stocks/${item.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-semibold text-primary">
                      {item.code}
                    </span>
                    <Badge
                      variant={
                        item.status === "completed" ? "default" : "secondary"
                      }
                      className="text-[10px]"
                    >
                      {item.status === "completed" ? "Selesai" : "Draft"}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{formatDate(item.date)}</span>
                    <span>{item.lineCount} barang</span>
                  </div>
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-xs font-semibold">
                      {formatCurrency(item.totalCost)}
                    </span>
                    {item.status === "draft" && canDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteId(item.id);
                        }}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead className="w-12">No</TableHead>
                  <TableHead>Nomor Dokumen</TableHead>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Jumlah Barang</TableHead>
                  <TableHead className="text-right">Total Nilai</TableHead>
                  <TableHead>Catatan</TableHead>
                  <TableHead className="w-20 text-center">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, idx) => (
                  <TableRow
                    key={item.id}
                    className="text-xs cursor-pointer hover:bg-muted/40"
                    onClick={() => navigate(`/opening-stocks/${item.id}`)}
                  >
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell className="font-mono font-semibold text-primary">
                      {item.code}
                    </TableCell>
                    <TableCell>{formatDate(item.date)}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          item.status === "completed" ? "default" : "secondary"
                        }
                        className="text-[10px]"
                      >
                        {item.status === "completed" ? "Selesai" : "Draft"}
                      </Badge>
                    </TableCell>
                    <TableCell>{item.lineCount} barang</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(item.totalCost)}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-muted-foreground">
                      {item.note || "—"}
                    </TableCell>
                    <TableCell
                      className="text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {item.status === "draft" && canDelete ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 text-destructive hover:text-destructive"
                          onClick={() => setDeleteId(item.id)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">
                          —
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Draft Dialog */}
      <Dialog
        open={Boolean(deleteId)}
        onOpenChange={(open) => !open && setDeleteId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hapus Draft Stok Awal?</DialogTitle>
            <DialogDescription>
              Dokumen draft ini akan dihapus permanen. Tindakan ini tidak dapat
              dibatalkan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Batal
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Menghapus..." : "Hapus Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
